/**
 * ARM iter316 — Tenant-level billing cadence toggle.
 *
 * Renders a small card that reads GET /api/admin/billing-cadence and
 * lets an admin flip between the two supported cycle policies:
 *   - first_of_month     — calendar-month cycles (historical default)
 *   - first_of_enrollment — day-of-month cycles anchored to start
 *
 * Embed inside EnrollmentsAdmin or any settings page.
 */
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export const BillingCadenceCard = () => {
  const [current, setCurrent] = useState(null);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/billing-cadence');
      setCurrent(data);
      setPending(data.default_billing_cadence);
    } catch (e) {
      toast.error('Failed to load billing cadence setting');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!pending || pending === current?.default_billing_cadence) return;
    setSaving(true);
    try {
      await api.put('/admin/billing-cadence', {
        default_billing_cadence: pending,
      });
      toast.success('Billing cadence updated', {
        description: 'New enrollments will use this cycle policy.',
      });
      await load();
    } catch (e) {
      toast.error('Failed to update billing cadence', {
        description: e?.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="billing-cadence-card">
        <CardContent className="flex items-center gap-2 py-6 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading billing cadence…
        </CardContent>
      </Card>
    );
  }

  const dirty = pending && pending !== current?.default_billing_cadence;

  return (
    <Card data-testid="billing-cadence-card" className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Enrollment billing cadence</CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Applies to <strong>new enrollments</strong>. Existing families keep
          their current next-billing-date until their next natural rollover.
          Per-enrollment overrides on the approval form take precedence.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup
          value={pending || 'first_of_month'}
          onValueChange={setPending}
          className="space-y-3"
        >
          <div className="flex items-start gap-3 rounded border border-slate-200 p-3">
            <RadioGroupItem
              value="first_of_month"
              id="cadence-first-of-month"
              data-testid="cadence-first-of-month"
              className="mt-1"
            />
            <Label htmlFor="cadence-first-of-month" className="flex-1 cursor-pointer">
              <div className="text-sm font-semibold text-slate-800">
                First of the month
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Mid-month starts get prorated to the following 1st. Every
                cycle bills on the 1st. (Historical default.)
              </div>
            </Label>
          </div>
          <div className="flex items-start gap-3 rounded border border-slate-200 p-3">
            <RadioGroupItem
              value="first_of_enrollment"
              id="cadence-first-of-enrollment"
              data-testid="cadence-first-of-enrollment"
              className="mt-1"
            />
            <Label htmlFor="cadence-first-of-enrollment" className="flex-1 cursor-pointer">
              <div className="text-sm font-semibold text-slate-800">
                First of enrollment
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                First cycle bills on the enrollment start day. Subsequent
                cycles preserve the day of month (Jan 31 → Feb 28/29).
              </div>
            </Label>
          </div>
        </RadioGroup>
        <div className="flex items-center justify-between pt-1">
          <div className="text-xs text-slate-500">
            Current tenant default:{' '}
            <span className="font-semibold text-slate-700" data-testid="billing-cadence-current">
              {current?.default_billing_cadence || 'first_of_month'}
            </span>
            {current?.explicitly_set === false && (
              <span className="ml-1 text-amber-700">(inheriting fallback)</span>
            )}
          </div>
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="billing-cadence-save"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving…
              </span>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BillingCadenceCard;
